import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import WebViewer from "@pdftron/webviewer";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { X } from "@phosphor-icons/react";
import { message } from "antd";
import {
  AssignLayout,
  AssignSidebar,
  AssignSidebarHeader,
  AssignSidebarTitle,
  AssignSidebarMeta,
  AssignSidebarBody,
  AssignSidebarFooter,
  DocumentViewerPane,
  ViewerToolbar,
  ViewerToggle,
  ViewerBody,
  MarkdownViewer,
  FieldGroup,
  FieldLabel,
  TextInput,
  DocNameRow,
  DocNameSuffix,
  SearchWrapper,
  SearchResults,
  SearchResultItem,
  SearchResultEmpty,
  ResultName,
  ResultMeta,
  SelectedCard,
  ClearBtn,
  AutoMatchBanner,
  AutoMatchHeader,
  AutoMatchLabel,
  ExplainPanel,
  ExplainSignals,
  ExplainSignal,
  ExplainReason,
  ExplainAltHeading,
  ExplainAltRow,
  MatterSelect,
  PrimaryBtn,
  SecondaryBtn,
  DangerBtn,
  BtnRow,
  KbdHint,
  EmptyState,
  EmptyTitle,
  EmptyText,
  SkeletonBlock,
  SkeletonRow,
  FailedFileNotice,
  FailedFileTitle,
  FailedFileDetail,
  FailedFileBanner,
} from "../MailIntakeStyled";
import ConfidencePill from "../../../components/ConfidencePill";
import {
  buildSuggestionExplanation,
  describeIntakeFailure,
  normalizeConfidence,
  pickAutoMatchMatter,
  pickViewerSource,
  reconcileAutoMatch,
} from "../utils";
import {
  claimNextItem,
  assignItem,
  skipItem,
  deleteItem,
  fetchMatterIdentity,
  searchMatters,
  clearMatterSearch,
  refreshIntakeCounts,
  refreshCurrentItem,
} from "../../../slice/MailIntakeSlice";
import {
  applyApryseFeatureRestrictions,
  getApryseExtension,
  getDocumentStreamingUrl,
} from "../../../components/Document/DocumentUtils";
import { releaseOffscreenPageMemory } from "../../../utils/releaseOffscreenPageMemory";

// Small presentational skeleton that stands in for the assign form while
// the next item is being claimed. Keeping the footer button-row ghosted
// (instead of "Loading next document..." text) preserves the visual
// rhythm of the sidebar between docs so the layout doesn't jump.
const AssignSidebarSkeleton = () => (
  <>
    <AssignSidebarHeader>
      <SkeletonBlock $w="60%" $h={12} />
      <SkeletonBlock $w={48} $h={12} />
    </AssignSidebarHeader>
    <AssignSidebarBody>
      <SkeletonRow>
        <SkeletonBlock $w="40%" $h={10} />
        <SkeletonBlock $h={44} />
      </SkeletonRow>
      <SkeletonRow>
        <SkeletonBlock $w="40%" $h={10} />
        <SkeletonBlock $h={36} />
      </SkeletonRow>
      <SkeletonRow>
        <SkeletonBlock $w="40%" $h={10} />
        <SkeletonBlock $h={36} />
      </SkeletonRow>
    </AssignSidebarBody>
    <AssignSidebarFooter>
      <SkeletonBlock $h={40} />
      <BtnRow>
        <SkeletonBlock $h={34} />
        <SkeletonBlock $h={34} />
      </BtnRow>
    </AssignSidebarFooter>
  </>
);

const DEFAULT_SKIP_REASONS = [
  { value: "client_not_found", label: "Can't find the client" },
  { value: "no_file", label: "Person has no file with us" },
  { value: "unreadable", label: "Can't read it" },
  { value: "other", label: "Other" },
];

const formatSkipDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "";

const AssignView = () => {
  const dispatch = useDispatch();
  const {
    currentItem,
    currentItemLoading,
    noMoreItems,
    assigning,
    matterSearchResults,
    matterSearchLoading,
    matterIdentity,
    matterIdentityLoading,
    counts,
  } = useSelector((state) => state.mailIntake);
  // Only the Skipped folder's reviewers may delete (Matthew, 2026-10-27);
  // everyone else files or skips with a reason.
  const canDelete = counts?.viewer?.can_delete === true;
  const skipReasons = counts?.viewer?.skip_reasons?.length
    ? counts.viewer.skip_reasons
    : DEFAULT_SKIP_REASONS;
  const [showSkipReasons, setShowSkipReasons] = useState(false);

  const [selectedMatter, setSelectedMatter] = useState(null);
  const [autoSuggestionDismissed, setAutoSuggestionDismissed] =
    useState(false);

  // Client-record card (Matthew, 2026-07-18): whenever a matter is
  // chosen — by the AI suggestion or a manual search pick — pull the
  // client's stored identifiers so the sorter can confirm the match
  // against the letter.
  useEffect(() => {
    if (selectedMatter?.id) {
      dispatch(fetchMatterIdentity(selectedMatter.id));
    }
  }, [dispatch, selectedMatter?.id]);
  const [documentName, setDocumentName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [viewMode, setViewMode] = useState("pdf");
  const markdownViewerRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Skip/Delete thunks don't have dedicated `pending` flags on the slice
  // the way `assigning` does, so we track in-flight state locally to
  // prevent rapid keypresses (keyboard auto-repeat on S/D in particular)
  // or double-clicks from firing concurrent dispatches on the same intake
  // item before `currentItem` swaps. Without this, holding S briefly can
  // silently skip multiple documents the user never sees.
  const [skipping, setSkipping] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const busy = assigning || skipping || deleting;
  const [viewerReady, setViewerReady] = useState(false);
  // Callback ref backing state — lets the init effect react to the container
  // actually mounting (so we aren't racing the first render).
  const [viewerContainer, setViewerContainer] = useState(null);

  const searchTimerRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const webViewerInstanceRef = useRef(null);
  const stopReleasingPageMemoryRef = useRef(null);
  const isViewerInitializing = useRef(false);
  const isMountedRef = useRef(false);
  // Monotonic token that's bumped every time the init effect cleans up.
  // An in-flight WebViewer() promise captures the token at start; when it
  // resolves, we compare against the current ref. A mismatch means the
  // container this init was bound to has already been unmounted (e.g.
  // user assigned/skipped before the async init finished), so the
  // resolved instance gets disposed instead of stored. Without this,
  // a stale instance bound to a now-destroyed DOM node would be stashed
  // in webViewerInstanceRef, and subsequent documents would render blank.
  const initGenerationRef = useRef(0);

  // Derive file info from the real API shape (original_filename) or mock
  // (file_name). document_name leads the fallback chain so it matches the
  // form-reset effect below, which reads
  // `document_name || original_filename || file_name` to seed the name
  // editor. If those two sources disagreed (e.g. a sorter saved
  // "Report.docx" as document_name but the upload was "scan.pdf"), the
  // editor would display "Report" with ".pdf" as the suffix and the assign
  // payload would silently drop the original ".docx" extension.
  const fileName =
    currentItem?.document_name ||
    currentItem?.original_filename ||
    currentItem?.file_name ||
    "";
  const fileExt = fileName.match(/\.[^/.]+$/)?.[0] || ".pdf";

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize Apryse WebViewer once the container DOM node is attached.
  // Using a callback-ref-backed state means this effect re-runs as soon as
  // the viewer <div> mounts (even if currentItem was null on first render),
  // which fixes the "empty deps array runs before container exists" race.
  useEffect(() => {
    if (!viewerContainer) return;
    if (webViewerInstanceRef.current || isViewerInitializing.current) return;

    const myGeneration = ++initGenerationRef.current;
    isViewerInitializing.current = true;

    WebViewer(
      {
        path: "/webviewer/lib/",
        workerPath: "/webviewer/lib/core/",
        licenseKey: process.env.REACT_APP_APRYSE_LICENSE,
        fullAPI: false,
        enableWebSocket: false,
        disabledElements: ["errorModal"],
        streaming: true,
        useDownloader: true,
      },
      viewerContainer
    )
      .then((instance) => {
        const isStale =
          !isMountedRef.current || myGeneration !== initGenerationRef.current;
        if (isStale) {
          // The container this init was bound to has been unmounted, or the
          // component itself is gone. Dispose the instance immediately so
          // we don't orphan it (and leave the flag alone — cleanup already
          // reset it for the next init, or nobody's listening anymore).
          try {
            instance.UI.dispose?.();
          } catch {
            /* cleanup best-effort */
          }
          return;
        }
        webViewerInstanceRef.current = instance;
        isViewerInitializing.current = false;
        stopReleasingPageMemoryRef.current = releaseOffscreenPageMemory(instance);

        instance.UI.setToolbarGroup("toolbarGroup-View");
        instance.Core.annotationManager.enableReadOnlyMode();
        applyApryseFeatureRestrictions(instance);

        instance.UI.disableElements([
          "toolbarGroup-Annotate",
          "toolbarGroup-Edit",
          "toolbarGroup-Shapes",
          "toolbarGroup-Insert",
          "toolbarGroup-Forms",
          "toolbarGroup-FillAndSign",
        ]);
        setViewerReady(true);
      })
      .catch((err) => {
        if (myGeneration !== initGenerationRef.current) return;
        console.error("Failed to initialize WebViewer:", err);
        isViewerInitializing.current = false;
      });

    return () => {
      // Bump the generation first so any in-flight WebViewer() promise
      // resolves into the "stale" branch above and disposes itself.
      initGenerationRef.current += 1;
      // Clear the in-flight flag so a subsequent container mount can
      // start a fresh init. This is the critical bit missing in the
      // previous version: without it, a fast assign-then-claim cycle
      // would leave the flag stuck true after the container unmounted
      // (instance ref was still null, so the old cleanup did nothing),
      // and the next init would bail out at the guard check.
      isViewerInitializing.current = false;
      stopReleasingPageMemoryRef.current?.();
      stopReleasingPageMemoryRef.current = null;
      if (webViewerInstanceRef.current) {
        try {
          webViewerInstanceRef.current.UI.dispose?.();
        } catch {
          /* cleanup best-effort */
        }
        webViewerInstanceRef.current = null;
        if (isMountedRef.current) setViewerReady(false);
      }
    };
  }, [viewerContainer]);

  // Load document into the viewer when the item (or its usable source)
  // changes, OR when the viewer becomes ready after currentItem has
  // already been set. Keyed by item id + source KIND rather than the
  // raw URL: each silent poll mints a fresh presigned URL for the same
  // object, and reloading on every rotation would flicker — but a poll
  // that brings a source where there was none (or upgrades stream ->
  // presigned) must load it (Bugbot #200: the pane stayed blank after
  // a refresh populated view_url on the same item).
  const loadedSourceKeyRef = useRef(null);
  // Pipeline verdict on the file itself. When the bytes are unusable we
  // never hand them to Apryse — it would spin forever — and show the
  // reason in the pane instead.
  const failure = describeIntakeFailure(currentItem);
  const fileUnusable = Boolean(failure?.fileUnusable);
  useEffect(() => {
    const instance = webViewerInstanceRef.current;
    if (!instance || !viewerReady || !currentItem) return;
    if (fileUnusable) {
      loadedSourceKeyRef.current = `${currentItem.id}:unusable`;
      return;
    }

    // Preference order lives in pickViewerSource (unit-tested):
    // presigned view_url first — mail-connector PDFs live in the mail
    // system's bucket, which the streaming service can't serve, so
    // preferring the staging document id blanked the sorter viewer on
    // firehose day one (2026-07-09). Streaming remains the fallback
    // for items with no pre-assignment view_url.
    const source = pickViewerSource(currentItem);
    const sourceKey = `${currentItem.id}:${source?.kind ?? "none"}`;
    if (loadedSourceKeyRef.current === sourceKey) return;
    loadedSourceKeyRef.current = sourceKey;

    if (source?.kind === "presigned") {
      instance.UI.loadDocument(source.value, {
        filename: fileName || `Document ${currentItem.id}`,
        extension: getApryseExtension(fileName),
      });
    } else if (source?.kind === "stream") {
      // Shared streaming-URL builder handles 'D'-prefix normalization
      // (unique_id style IDs like "D42" are stripped to "42").
      const streamUrl = getDocumentStreamingUrl(source.value);
      instance.UI.loadDocument(streamUrl, {
        filename: fileName || `Document ${currentItem.id}`,
        streaming: true,
        extension: getApryseExtension(fileName),
      });
    }
  }, [currentItem, fileName, fileUnusable, viewerReady]);

  // Poll the open item while its Phase A pipeline is still running, so
  // the Markdown tab flips from "still reading" to real text — and the
  // auto-match suggestion lands — without the sorter re-claiming
  // (Bugbot #199: currentItem was set once at claim and never
  // refreshed). Bounded: extraction typically lands in 1–2 minutes;
  // stop after ~8 so a stuck row can't poll forever.
  useEffect(() => {
    const status = currentItem?.intake_pipeline_status;
    const inFlight =
      status === "not_started" || status === "queued" || status === "extracting";
    if (!currentItem?.id || !inFlight) return;

    const MAX_POLLS = 60;
    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      if (polls > MAX_POLLS) {
        clearInterval(timer);
        return;
      }
      dispatch(refreshCurrentItem(currentItem.id));
    }, 8000);
    return () => clearInterval(timer);
  }, [dispatch, currentItem?.id, currentItem?.intake_pipeline_status]);

  // Markdown has its own scroll container. Always start at the raw
  // extraction's first line when the sorter opens the tab or advances
  // to another item. Without this, React reused the pane and preserved
  // the prior document's scroll position.
  useEffect(() => {
    if (viewMode === "markdown" && markdownViewerRef.current) {
      markdownViewerRef.current.scrollTop = 0;
    }
  }, [viewMode, currentItem?.id]);

  // Claim first document on mount
  useEffect(() => {
    if (!currentItem && !currentItemLoading && !noMoreItems) {
      dispatch(claimNextItem());
    }
  }, [dispatch, currentItem, currentItemLoading, noMoreItems]);

  // Reset form when a new document is claimed
  useEffect(() => {
    if (currentItem) {
      const fn = currentItem.document_name || currentItem.original_filename || currentItem.file_name || "";
      const nameWithoutExt = fn.replace(/\.[^/.]+$/, "");
      setDocumentName(nameWithoutExt);
      // Production API uses flat `auto_match_matter*` fields; older
      // mocks used nested `auto_match.matters`. Normalize both. A
      // READY suggestion should arrive pre-selected so the sorter only
      // needs to verify + press Assign (firehose day one, 2026-07-10).
      setSelectedMatter(pickAutoMatchMatter(currentItem));
      setAutoSuggestionDismissed(false);
      setSearchQuery("");
      setShowSearchResults(false);
      setShowDeleteConfirm(false);
      setShowSkipReasons(false);
      setViewMode("pdf");
      dispatch(clearMatterSearch());

    }
  }, [currentItem?.id, dispatch]);

  // An item can be claimed while Phase A is still extracting; the
  // silent refresh later brings the suggestion on the SAME item id.
  // Populate only when the sorter has not already made a manual
  // selection — human choice always wins.
  useEffect(() => {
    const suggestion = pickAutoMatchMatter(currentItem);
    setSelectedMatter((existing) =>
      reconcileAutoMatch({
        existingMatter: existing,
        suggestion,
        dismissed: autoSuggestionDismissed,
      }),
    );
  }, [currentItem, autoSuggestionDismissed]);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSearchResults(true);

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.trim().length >= 2) {
      searchTimerRef.current = setTimeout(() => {
        dispatch(searchMatters(value.trim()));
      }, 300);
    } else {
      dispatch(clearMatterSearch());
    }
  };

  const handleSelectMatter = useCallback(
    (matter) => {
      setSelectedMatter(matter);
      setSearchQuery("");
      setShowSearchResults(false);
      dispatch(clearMatterSearch());
    },
    [dispatch]
  );

  const handleAutoMatchClick = useCallback(() => {
    const suggestion = pickAutoMatchMatter(currentItem);
    if (suggestion) {
      setAutoSuggestionDismissed(false);
      setSelectedMatter(suggestion);
    }
  }, [currentItem]);

  // Every queue-mutating action (assign / skip / delete) is followed by a
  // fresh claim and a counts refresh so the tab badges on MailIntake.js stay
  // live. The counts thunk is cheap (single status-grouped query) and the
  // badges otherwise drift arbitrarily far from reality between mounts.
  const handleAssign = useCallback(async () => {
    if (!currentItem || !selectedMatter || assigning || skipping || deleting) {
      return;
    }
    await dispatch(
      assignItem({
        itemId: currentItem.id,
        matterId: selectedMatter.id,
        documentName: documentName ? `${documentName}${fileExt}` : undefined,
      })
    );
    dispatch(claimNextItem());
    dispatch(refreshIntakeCounts());
  }, [
    dispatch,
    currentItem,
    selectedMatter,
    assigning,
    skipping,
    deleting,
    documentName,
    fileExt,
  ]);

  const handleSkip = useCallback(
    async (reason) => {
      if (!currentItem || skipping || assigning || deleting) return;
      setSkipping(true);
      try {
        const result = await dispatch(
          skipItem({ itemId: currentItem.id, reason })
        );
        if (skipItem.rejected.match(result)) {
          message.error(result.payload || "Could not skip this document");
          return;
        }
        setShowSkipReasons(false);
        dispatch(claimNextItem());
        dispatch(refreshIntakeCounts());
      } finally {
        setSkipping(false);
      }
    },
    [dispatch, currentItem, skipping, assigning, deleting]
  );

  const handleDelete = useCallback(async () => {
    if (!currentItem || !canDelete || deleting || assigning || skipping) {
      return;
    }
    setDeleting(true);
    try {
      const result = await dispatch(deleteItem(currentItem.id));
      if (deleteItem.rejected.match(result)) {
        message.error(result.payload || "Could not delete this document");
        return;
      }
      setShowDeleteConfirm(false);
      dispatch(claimNextItem());
      dispatch(refreshIntakeCounts());
    } finally {
      setDeleting(false);
    }
  }, [dispatch, currentItem, canDelete, deleting, assigning, skipping]);

  // Keyboard shortcuts. Intentionally scoped to page-level keydowns only
  // (bail if the user is typing in an input/select/textarea or a modifier
  // is held), otherwise searching for matters with "Skip" in the name
  // would trigger the skip shortcut mid-type.
  //
  //   Enter → Assign (when a matter is selected)
  //   S     → Skip: opens the reason list; 1-4 picks a reason, Esc closes
  //   D     → Delete, reviewers only (first press asks, second confirms)
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!currentItem) return;

      if (showSkipReasons) {
        if (e.key === "Escape") {
          e.preventDefault();
          setShowSkipReasons(false);
          return;
        }
        const picked = skipReasons[Number(e.key) - 1];
        if (picked && !busy) {
          e.preventDefault();
          handleSkip(picked.value);
        }
        return;
      }
      if (e.key === "Enter" && selectedMatter && !busy) {
        e.preventDefault();
        handleAssign();
        return;
      }
      const k = e.key.toLowerCase();
      if (k === "s" && !busy) {
        e.preventDefault();
        setShowDeleteConfirm(false);
        setShowSkipReasons(true);
        return;
      }
      if (k === "d" && canDelete && !busy) {
        e.preventDefault();
        if (showDeleteConfirm) handleDelete();
        else setShowDeleteConfirm(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    handleAssign,
    handleSkip,
    handleDelete,
    selectedMatter,
    busy,
    currentItem,
    showDeleteConfirm,
    showSkipReasons,
    skipReasons,
    canDelete,
  ]);

  // Terminal empty-queue: no further documents are coming, so it is
  // safe to tear down the viewer here. The render path below keeps the
  // WebViewer instance mounted across the much more frequent
  // assign/skip/delete → claim-next transitions, where `currentItem`
  // briefly flips to null before the next item arrives.
  if (noMoreItems && !currentItem) {
    return (
      <AssignLayout>
        <EmptyState>
          <EmptyTitle>Queue is empty</EmptyTitle>
          <EmptyText>All documents have been processed. New documents will appear here as they arrive.</EmptyText>
        </EmptyState>
      </AssignLayout>
    );
  }

  // Optional chain — `currentItem` is null during the brief assign→claim
  // gap and on initial mount before the first claim resolves.
  const autoMatch = currentItem?.auto_match;

  // "Why did the AI pick this matter?" — rendered from the matcher's
  // operator-visible audit trail (match_candidates_resolved). Null when
  // the item has no ranked candidates at all.
  const explanation = buildSuggestionExplanation(currentItem);

  return (
    <AssignLayout>
      {/* --- Left: Assignment form (or loading placeholder between docs) --- */}
      <AssignSidebar>
        {currentItem ? (
          <>
        <AssignSidebarHeader>
          <AssignSidebarTitle>Assign Document</AssignSidebarTitle>
          <AssignSidebarMeta>
            {currentItem.page_count != null ? `${currentItem.page_count} pages` : ""}
          </AssignSidebarMeta>
        </AssignSidebarHeader>

        <AssignSidebarBody>
          {currentItem.last_skip && (
            <ExplainPanel data-testid="skip-history">
              <ExplainReason style={{ fontWeight: 600 }}>
                Skipped{currentItem.last_skip.by_name ? ` by ${currentItem.last_skip.by_name}` : ""}
                {currentItem.last_skip.at ? ` on ${formatSkipDate(currentItem.last_skip.at)}` : ""}
              </ExplainReason>
              <ExplainReason>{currentItem.last_skip.reason_label}</ExplainReason>
              {currentItem.last_skip.note && (
                <ExplainReason>{currentItem.last_skip.note}</ExplainReason>
              )}
            </ExplainPanel>
          )}

          {/* Pipeline failure: say what is wrong beside the Delete button
              (Matthew, 2026-10-08) instead of a silent spinner. */}
          {failure && (
            <FailedFileBanner role="alert" data-testid="failed-file-banner">
              <strong>{failure.title}</strong>
              <span>{failure.detail}</span>
              <span>{failure.suggestion}</span>
            </FailedFileBanner>
          )}

          {/* Auto-match banner */}
          {autoMatch?.type === "single_matter" && !selectedMatter && (
            <AutoMatchBanner matchType="single_matter" onClick={handleAutoMatchClick}>
              <AutoMatchHeader>
                <AutoMatchLabel matchType="single_matter">
                  Auto-matched
                </AutoMatchLabel>
                <ConfidencePill value={normalizeConfidence(autoMatch.confidence)} />
              </AutoMatchHeader>
              <ResultName>{autoMatch.client_name}</ResultName>
              <ResultMeta>
                {autoMatch.matters[0].case_number} · {autoMatch.matters[0].practice_area}
              </ResultMeta>
            </AutoMatchBanner>
          )}

          {autoMatch?.type === "multi_matter" && !selectedMatter && (
            <AutoMatchBanner matchType="multi_matter">
              <AutoMatchHeader>
                <AutoMatchLabel matchType="multi_matter">
                  Client found — select matter
                </AutoMatchLabel>
                <ConfidencePill value={normalizeConfidence(autoMatch.confidence)} />
              </AutoMatchHeader>
              <ResultName>{autoMatch.client_name}</ResultName>
              <ResultMeta>{autoMatch.matters.length} open matters</ResultMeta>
            </AutoMatchBanner>
          )}

          {autoMatch?.type === "multi_matter" && !selectedMatter && (
            <FieldGroup>
              <FieldLabel>Select Matter</FieldLabel>
              <MatterSelect
                defaultValue=""
                onChange={(e) => {
                  const m = autoMatch.matters.find((x) => x.id === e.target.value);
                  if (m) handleSelectMatter(m);
                }}
              >
                <option value="" disabled>
                  Choose a matter...
                </option>
                {autoMatch.matters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.case_number} — {m.practice_area}
                  </option>
                ))}
              </MatterSelect>
            </FieldGroup>
          )}

          {/* Search matters */}
          <FieldGroup>
            <FieldLabel>Search Matters</FieldLabel>
            {selectedMatter ? (
              <SelectedCard>
                <div>
                  {/* Full Matter display name first (Matthew,
                      2026-07-20: selecting from search showed only
                      the client's name); client name stays as the
                      fallback for auto-match payloads that carry no
                      display_name. */}
                  <ResultName>{selectedMatter.display_name || selectedMatter.client_name}</ResultName>
                  <ResultMeta>
                    {selectedMatter.is_auto_match &&
                    selectedMatter.auto_match_confidence != null
                      ? `AI suggestion · ${Math.round(
                          selectedMatter.auto_match_confidence * 100,
                        )}%`
                      : null}
                    {selectedMatter.case_number || selectedMatter.sf_id}
                    {selectedMatter.practice_area ? ` · ${selectedMatter.practice_area}` : ""}
                    {selectedMatter.ssn_last_four ? ` · SSN ****${selectedMatter.ssn_last_four}` : ""}
                    {selectedMatter.va_file_number ? ` · VA#${selectedMatter.va_file_number}` : ""}
                  </ResultMeta>
                </div>
                <ClearBtn
                  aria-label="Clear selected matter"
                  onClick={() => {
                    setSelectedMatter(null);
                    if (selectedMatter.is_auto_match) {
                      // Explicit human rejection: polling the same item
                      // must not put the suggestion back (Bugbot #204).
                      setAutoSuggestionDismissed(true);
                    }
                  }}
                >
                  <X size={14} weight="bold" />
                </ClearBtn>
              </SelectedCard>
            ) : (
              <SearchWrapper ref={searchWrapperRef}>
                <TextInput
                  placeholder="Name, SSN last 4, VA file #, case #..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (matterSearchResults.length > 0) setShowSearchResults(true);
                  }}
                />
                {showSearchResults &&
                  (matterSearchResults.length > 0 || matterSearchLoading) && (
                    <SearchResults>
                      {matterSearchLoading ? (
                        <SearchResultEmpty>Searching...</SearchResultEmpty>
                      ) : (
                        matterSearchResults.map((matter) => (
                          <SearchResultItem
                            key={matter.id}
                            onClick={() => handleSelectMatter(matter)}
                          >
                            <ResultName>{matter.display_name || matter.client_name}</ResultName>
                            <ResultMeta>
                              {matter.case_type || ""}
                              {matter.status ? ` · ${matter.status}` : ""}
                            </ResultMeta>
                          </SearchResultItem>
                        ))
                      )}
                    </SearchResults>
                  )}
              </SearchWrapper>
            )}
          </FieldGroup>

          {/* Document name */}
          <FieldGroup>
            <FieldLabel>Document Name</FieldLabel>
            <DocNameRow>
              <TextInput
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
              />
              <DocNameSuffix>{fileExt}</DocNameSuffix>
            </DocNameRow>
          </FieldGroup>

          {/* Why the AI suggested this matter (Matthew, 2026-07-13).
              Rendered from the matcher's audit trail so the sorter can
              verify the evidence instead of trusting a bare percentage. */}
          {explanation && (
            <FieldGroup>
              <FieldLabel>
                {explanation.primary
                  ? "Why this suggestion"
                  : "Candidates considered"}
              </FieldLabel>
              <ExplainPanel data-testid="suggestion-explanation">
                {explanation.primary && (
                  <>
                    {explanation.primary.signals.length > 0 && (
                      <ExplainSignals>
                        {explanation.primary.signals.map((signal) => (
                          <ExplainSignal key={signal}>{signal}</ExplainSignal>
                        ))}
                      </ExplainSignals>
                    )}
                    {explanation.primary.reasons.map((line) => (
                      <ExplainReason key={line}>{line}</ExplainReason>
                    ))}
                  </>
                )}
                {explanation.alternates.length > 0 && (
                  <>
                    <ExplainAltHeading>
                      {explanation.primary
                        ? "Also considered"
                        : "Ranked candidates"}
                    </ExplainAltHeading>
                    {explanation.alternates.map((alt, index) => (
                      <ExplainAltRow key={`${alt.matterName}-${index}`}>
                        <span>{alt.matterName}</span>
                        {alt.confidence != null && (
                          <ConfidencePill value={alt.confidence} />
                        )}
                      </ExplainAltRow>
                    ))}
                  </>
                )}
              </ExplainPanel>
            </FieldGroup>
          )}

          {/* Client record for match confirmation (Matthew, 2026-07-18):
              the firm's stored identifiers for the chosen matter's
              client, so the human can compare against the letter
              before assigning. */}
          {selectedMatter && (matterIdentity?.client || matterIdentityLoading) && (
            <FieldGroup>
              <FieldLabel>Client record</FieldLabel>
              <ExplainPanel data-testid="client-identity-card">
                {matterIdentityLoading && !matterIdentity?.client ? (
                  <ExplainReason>Loading client record…</ExplainReason>
                ) : (
                  <>
                    <ExplainReason style={{ fontWeight: 600 }}>
                      {matterIdentity.client.name}
                    </ExplainReason>
                    {[
                      ["Date of birth", matterIdentity.client.date_of_birth],
                      [
                        "SSN",
                        matterIdentity.client.ssn_last_four
                          ? `•••-••-${matterIdentity.client.ssn_last_four}`
                          : "",
                      ],
                      ["VA file #", matterIdentity.client.va_file_number],
                      [
                        "Phone",
                        (matterIdentity.client.phones || []).join(" · "),
                      ],
                      ["Email", matterIdentity.client.email],
                    ]
                      .filter(([, value]) => Boolean(value))
                      .map(([label, value]) => (
                        <ExplainAltRow key={label}>
                          <span style={{ color: "#7688A3" }}>{label}</span>
                          <span>{value}</span>
                        </ExplainAltRow>
                      ))}
                    {(matterIdentity.client.addresses || []).map((entry) => (
                      <ExplainAltRow key={`${entry.label}-${entry.address}`}>
                        <span style={{ color: "#7688A3" }}>
                          {entry.label} address
                        </span>
                        <span style={{ textAlign: "right" }}>
                          {entry.address}
                        </span>
                      </ExplainAltRow>
                    ))}
                  </>
                )}
              </ExplainPanel>
            </FieldGroup>
          )}
        </AssignSidebarBody>

        <AssignSidebarFooter>
          <PrimaryBtn onClick={handleAssign} disabled={!selectedMatter || busy}>
            {assigning ? "Assigning..." : "Assign"}
            {!busy && <KbdHint aria-label="Enter key">Enter</KbdHint>}
          </PrimaryBtn>
          {showSkipReasons ? (
            <div
              data-testid="skip-reasons"
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            >
              <FieldLabel>Why are you skipping it?</FieldLabel>
              {skipReasons.map((reason, index) => (
                <SecondaryBtn
                  key={reason.value}
                  onClick={() => handleSkip(reason.value)}
                  disabled={busy}
                >
                  {reason.label}
                  {!busy && <KbdHint aria-label={`Shortcut: ${index + 1}`}>{index + 1}</KbdHint>}
                </SecondaryBtn>
              ))}
              <SecondaryBtn onClick={() => setShowSkipReasons(false)} disabled={busy}>
                {skipping ? "Skipping..." : "Cancel"}
                {!busy && <KbdHint aria-label="Shortcut: Escape">Esc</KbdHint>}
              </SecondaryBtn>
            </div>
          ) : (
            <BtnRow>
              <SecondaryBtn
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setShowSkipReasons(true);
                }}
                disabled={busy}
              >
                Skip
                {!busy && <KbdHint aria-label="Shortcut: S">S</KbdHint>}
              </SecondaryBtn>
              {canDelete &&
                (showDeleteConfirm ? (
                  <DangerBtn onClick={handleDelete} disabled={busy}>
                    {deleting ? "Deleting..." : "Confirm Delete"}
                    {!busy && (
                      <KbdHint aria-label="Shortcut: D to confirm">D</KbdHint>
                    )}
                  </DangerBtn>
                ) : (
                  <DangerBtn
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={busy}
                  >
                    Delete
                    {!busy && <KbdHint aria-label="Shortcut: D">D</KbdHint>}
                  </DangerBtn>
                ))}
            </BtnRow>
          )}
        </AssignSidebarFooter>
          </>
        ) : (
          <AssignSidebarSkeleton />
        )}
      </AssignSidebar>

      {/* --- Right: Document viewer (permanently mounted) ---
          Keeping this subtree outside the `currentItem` conditional is
          what prevents the WebViewer instance from being torn down
          between every document. The container div itself survives
          assign/skip/delete → claim-next transitions, so the Apryse
          instance stays alive and we only call loadDocument() for
          each new item instead of re-bootstrapping Apryse (1-3s). */}
      <DocumentViewerPane>
        <ViewerToolbar>
          <span style={{ fontWeight: 600 }}>{fileName || "\u00A0"}</span>
          <div style={{ display: "flex", gap: 4 }}>
            <ViewerToggle active={viewMode === "pdf"} onClick={() => setViewMode("pdf")}>
              PDF
            </ViewerToggle>
            <ViewerToggle active={viewMode === "markdown"} onClick={() => setViewMode("markdown")}>
              Markdown
            </ViewerToggle>
          </div>
        </ViewerToolbar>

        <ViewerBody>
          {/* PDF container is always mounted so the WebViewer instance
              keeps its DOM node across view-mode toggles. We hide it
              with CSS rather than unmounting, because unmounting would
              destroy the iframe the Apryse instance is bound to. */}
          <div
            ref={setViewerContainer}
            style={{
              width: "100%",
              height: "100%",
              display: viewMode === "pdf" && !fileUnusable ? "block" : "none",
            }}
          />
          {viewMode === "pdf" && fileUnusable && currentItem && (
            <FailedFileNotice data-testid="failed-file-notice">
              <FailedFileTitle>{failure.title}</FailedFileTitle>
              <FailedFileDetail>{failure.detail}</FailedFileDetail>
              <FailedFileDetail>{failure.suggestion}</FailedFileDetail>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {canDelete &&
                  (showDeleteConfirm ? (
                    <DangerBtn onClick={handleDelete} disabled={busy}>
                      {deleting ? "Deleting..." : "Confirm Delete"}
                    </DangerBtn>
                  ) : (
                    <DangerBtn
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={busy}
                    >
                      Delete this file
                    </DangerBtn>
                  ))}
                <SecondaryBtn
                  onClick={() => handleSkip("unreadable")}
                  disabled={busy}
                >
                  {skipping ? "Skipping..." : "Skip: can't read it"}
                </SecondaryBtn>
              </div>
            </FailedFileNotice>
          )}
          {viewMode === "markdown" && currentItem && (
            <MarkdownViewer ref={markdownViewerRef}>
              <h1>Extracted Document Content</h1>
              <p>
                <strong>File:</strong> {fileName}
                <br />
                <strong>Pages:</strong> {currentItem.page_count ?? "—"}
              </p>
              <hr />
              {currentItem.extracted_markdown ? (
                // What the matching engine actually read — useful for
                // sanity-checking why a piece of mail matched (or didn't).
                // remarkGfm matches the app's other markdown surfaces
                // (BriefingMarkdown): pipeline output uses GFM tables.
                <Markdown remarkPlugins={[remarkGfm]}>
                  {currentItem.extracted_markdown}
                </Markdown>
              ) : currentItem.intake_pipeline_status === "extracting" ||
                currentItem.intake_pipeline_status === "queued" ||
                currentItem.intake_pipeline_status === "not_started" ? (
                <p>
                  The AI is still reading this document — extracted text will
                  appear here in a minute or two.
                </p>
              ) : (
                <p>
                  No extracted text is available for this document
                  {failure ? ` — ${failure.detail}` : "."}
                </p>
              )}
            </MarkdownViewer>
          )}
        </ViewerBody>
      </DocumentViewerPane>
    </AssignLayout>
  );
};

export default AssignView;
